import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Banknote, Check } from "lucide-react";
import { cn } from "@shared/utils/cn";
import { sanitizeHtml } from "@shared/utils/sanitize";
import { menuPriceLabel, menuUsdPriceLabel } from "@shared/utils/menuPrice";
import type { TimeSlotMenu } from "@features/booking/types/timeSlots";
import { UnavailableExperienceAlternatives } from './UnavailableExperienceAlternatives';

interface ExperienceListProps {
  menus: TimeSlotMenu[];
  selectedMenuId: string | null;
  /** ID of the menu whose detail panel is expanded */
  expandedMenuId: string | null;
  onToggleExpand: (menuId: string) => void;
  /** Currently selected time, for the "Not available at {{time}}" label */
  selectedTime?: string | null;
  /** menuId → same-day times where the (disabled) menu is bookable */
  alternativeTimes?: Record<number, string[]>;
  /** Switch the booking to a time and select the experience in place */
  onSwitchTime?: (menuId: number, time: string) => void;
  /** Visitor from abroad at a venue whose foreign cards pay in USD */
  showUsd?: boolean;
}

const ExperienceList = ({
  menus,
  selectedMenuId,
  expandedMenuId,
  onToggleExpand,
  selectedTime,
  alternativeTimes,
  onSwitchTime,
  showUsd = false,
}: ExperienceListProps) => {
  const { t } = useTranslation('booking');
  const [showDetailsMenuId, setShowDetailsMenuId] = useState<string | null>(null);

  // Auto-hide details when the expanded menu changes
  useEffect(() => {
    setShowDetailsMenuId(null);
  }, [expandedMenuId]);

  if (menus.length === 0) {
    return (
      <div className="w-full text-center py-8 text-foreground/50">
        {t('EXPERIENCE_NO_EXPERIENCE')}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 px-6">
      {menus.map((menu) => {
        const menuIdStr = String(menu.id);
        const isSelected = menuIdStr === selectedMenuId;
        const isExpanded = menuIdStr === expandedMenuId;
        const isDisabled = menu.disabled;
        const alts = alternativeTimes?.[menu.id] ?? [];
        const hasAlternatives = !menu.preferredAccess && alts.length > 0;
        const showAlternatives = hasAlternatives && !!onSwitchTime;
        // Recoverable: unavailable at the chosen time but bookable at others.
        // Present it as a real option (full opacity, normal text) — only the
        // truly-unavailable / preferred-access cards get the dimmed "dead" look.
        // The card itself stays non-clickable (gated on isDisabled); the
        // alternative-time chips are the action.
        const isRecoverable = isDisabled && showAlternatives;
        const dimmed = isDisabled && !showAlternatives;
        const priceLabel = (showUsd && menuUsdPriceLabel(menu)) || menuPriceLabel(menu);

        return (
          <div
            key={menu.id}
            data-testid={`experience-${menu.id}`}
            className={cn(
              // bg-card is the base on every state so translucent themes don't
              // bleed the body image through the card. Tinted states layer the
              // accent on top via a `::before` overlay (`relative` + `before:`
              // pseudo) — using `bg-brand/5` alone would replace bg-card and
              // dissolve back into the translucent chrome.
              "relative rounded-xl border-2 transition-all overflow-hidden bg-card",
              "before:absolute before:inset-0 before:pointer-events-none before:transition-colors",
              dimmed
                ? "border-border/50 translucent:border-surface-border opacity-60 translucent:opacity-100 translucent:before:bg-foreground/15"
                : isRecoverable
                  ? "border-border translucent:border-surface-border cursor-default"
                  : isSelected
                    ? "border-brand before:bg-brand/10"
                    : "border-border translucent:border-surface-border hover:border-brand/50 cursor-pointer",
            )}
            onClick={() => !isDisabled && onToggleExpand(menuIdStr)}
            role={isDisabled ? undefined : "button"}
            tabIndex={isDisabled ? undefined : 0}
            onKeyDown={(e) => {
              if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onToggleExpand(menuIdStr);
              }
            }}
          >
            <div className="flex">
              {/* Future image slot */}

              <div className="flex-1 p-4 sm:p-5">
                {/* Header: name + selected badge */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className={cn(
                      "text-base font-medium",
                      dimmed ? "text-foreground/40" : "text-foreground",
                    )}>
                      {menu.name}
                    </h3>
                    {menu.eventMenu && (
                      <span className="text-xs text-brand font-medium uppercase tracking-wide">{t('CALENDAR_LEGEND_EVENT')}</span>
                    )}
                    {isDisabled && menu.preferredAccess && (
                      <p className="text-xs text-foreground/40 mt-0.5">{t('AVAILABLE_TABLES_PREFERRED_ACCESS')}</p>
                    )}
                    {isDisabled && !menu.preferredAccess && showAlternatives && (
                      <p className="text-xs text-foreground/60 mt-0.5">{t('EXPERIENCE_UNAVAILABLE_AT_TIME', { time: selectedTime ?? '' })}</p>
                    )}
                    {isDisabled && !menu.preferredAccess && !showAlternatives && (
                      <p className="text-xs text-foreground/40 mt-0.5">{t('STRING_NOT_AVAILABLE')}</p>
                    )}
                  </div>

                  {isSelected && !isDisabled && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand flex items-center justify-center">
                      <Check size={14} className="text-brand-foreground" />
                    </div>
                  )}
                </div>

                {/* Price */}
                {priceLabel && (
                  <div className={cn(
                    "flex items-center gap-1.5 mt-2 text-sm",
                    dimmed ? "text-foreground/30" : "text-foreground/60",
                  )}>
                    <Banknote size={16} className="flex-shrink-0" />
                    <span>{priceLabel} {t('BUY_A_GIFTCARD_STRING_3')}</span>
                  </div>
                )}

                {/* Expanded detail panel */}
                {isExpanded && !isDisabled && (
                  <div className="mt-3 space-y-3">
                    {menu.description && (
                      <div
                        className="text-sm text-foreground/60 prose prose-sm max-w-none [&_p]:my-1"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(menu.description) }}
                      />
                    )}
                    {menu.details && (
                      <div className="border-t border-border/50 pt-3">
                        {showDetailsMenuId === menuIdStr && (
                          <div
                            className="text-sm text-foreground/60 prose prose-sm max-w-none [&_p]:my-1 mb-2"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(menu.details) }}
                          />
                        )}
                        <span
                          className="text-sm text-foreground/60 underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDetailsMenuId((prev) => prev === menuIdStr ? null : menuIdStr);
                          }}
                        >
                          {showDetailsMenuId === menuIdStr
                            ? t('NEW_MODAL_HIDE_DETAILS')
                            : t('NEW_MODAL_VIEW_DETAILS')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {showAlternatives && (
                  <UnavailableExperienceAlternatives
                    times={alts}
                    onPick={(time) => onSwitchTime(menu.id, time)}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExperienceList;
